/**
 * Regras financeiras portadas do sistema 4.x (função `ch` do VisitForm, index.html).
 * Ficam isoladas aqui para poderem ser testadas e para que a mesma regra possa ser
 * reaproveitada no backend, se um dia a proposta precisar ser recalculada lá.
 */

export const num = (v: unknown): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

/** Arredonda em 2 casas devolvendo string, como o `.toFixed(2)` do 4.x. */
const duasCasas = (n: number) => n.toFixed(2);

/**
 * true quando nenhum dos campos de entrada foi preenchido.
 * Nesse caso o derivado fica vazio em vez de "0.00" — assim o campo somente
 * leitura aparece em branco no formulário e vai como NULL para o banco
 * ("não informado"), em vez de zero ("é de graça"). Mesmo comportamento do 4.x,
 * onde o derivado só era calculado depois que algum campo de origem era editado.
 */
const nenhumPreenchido = (...vals: unknown[]) =>
  vals.every((v) => v === null || v === undefined || v === "");

/** Valor com condição exclusiva = valor − valor × pct/100 */
export const aplicarDesconto = (valor: unknown, percentual: unknown): string => {
  if (nenhumPreenchido(valor, percentual)) return "";
  const v = num(valor);
  return duasCasas(v - (v * num(percentual)) / 100);
};

/**
 * Percentual do desconto por pagamento até o vencimento.
 *
 * **O valor que a família paga não muda.** O que muda é o tamanho da punição
 * por atrasar.
 *
 * Antes: mensalidade de 1.787,97 com 25% → paga 1.340,98 em dia, e o boleto
 * dizia que atrasar custava 446,99 a mais. A família olhava esse número e se
 * assustava — 33% de aumento por perder uma data.
 *
 * Agora: continua pagando 1.340,98 em dia. O desconto por pontualidade
 * anunciado é 15% dos 446,99, ou seja 67,05 — e é isso que ela perde se
 * atrasar. O valor cheio de tabela deixa de ser a referência do atraso.
 *
 * Quem manda de verdade é a API (`DESCONTO_ANTECIPADO_PCT`); este valor só é
 * usado enquanto a configuração não chega do servidor.
 */
export const PCT_ANTECIPADO_PADRAO = 15;

/**
 * Texto único do alerta de piso — a frase é a que a direção definiu, e aparece
 * igual na tela e na resposta da API. Duplicar o texto abriria a porta para a
 * tela dizer uma coisa e o servidor outra.
 */
export const AVISO_ABAIXO_DO_MINIMO =
  "Condição exclusiva abaixo do mínimo, proposta recusada por divergência de valores";

/** O mínimo que interessa aqui é uma linha da tabela de valores. */
interface LinhaValor {
  id: string;
  tipo: string;
  valor: string;
  ativo?: boolean;
  curso: { id: string } | null;
  referenciaId?: string | null;
}

/**
 * Piso que vale para a mensalidade escolhida.
 *
 * O piso é cadastrado **por linha de mensalidade**, não por curso. O mesmo
 * curso tem preços diferentes — Fundamental II com bilíngue e sem bilíngue,
 * parcial e integral — e um piso único por curso comparava a mensalidade
 * bilíngue de 1.961,31 com o piso da versão sem bilíngue, reprovando proposta
 * que estava correta.
 *
 * O caminho é: valor escolhido → linha(s) de mensalidade com esse valor
 * naquele curso → piso que aponta para elas. Casar pelo valor atual (e não
 * guardar o id na proposta) é o que faz a conta continuar certa ao reabrir uma
 * visita antiga e ao reajustar a tabela.
 *
 * Devolve `null` quando não há piso cadastrado, e nesse caso **nada é
 * barrado** — travar por falta de cadastro pararia a escola no dia em que
 * alguém criasse uma linha de mensalidade nova e esquecesse o piso.
 *
 * Havendo mais de um piso candidato, vence o mais alto: piso é proteção, e na
 * dúvida protege mais.
 */
export function minimoDaMensalidade(
  valores: LinhaValor[],
  valorNormal: unknown,
  cursoId: string,
): number | null {
  const escolhido = num(valorNormal);
  if (!(escolhido > 0)) return null;

  const mensalidades = valores.filter(
    (v) =>
      v.tipo === "MENSALIDADE" &&
      v.ativo !== false &&
      centavos(num(v.valor)) === centavos(escolhido) &&
      // mesma regra do select que ofereceu o valor: linha do curso ou linha sem curso
      (!v.curso || !cursoId || v.curso.id === cursoId),
  );
  if (!mensalidades.length) return null;

  const ids = new Set(mensalidades.map((v) => v.id));
  const pisos = valores.filter(
    (v) => v.tipo === "MINIMO" && v.ativo !== false && v.referenciaId && ids.has(v.referenciaId),
  );
  if (!pisos.length) return null;
  return Math.max(...pisos.map((v) => num(v.valor)));
}

/** Centavos inteiros — comparar reais em ponto flutuante erra no limite. */
const centavos = (n: number) => Math.round(n * 100);

/**
 * A proposta furou o piso?
 *
 * Compara em centavos inteiros para não deixar o binário do ponto flutuante
 * decidir: com `<` direto, um valor de 980,00 contra piso 980,00 pode virar
 * 979.9999999 e reprovar uma proposta que está exatamente no limite.
 */
export function abaixoDoMinimo(valorComDesconto: unknown, minimo: number | null): boolean {
  if (minimo === null || !(minimo > 0)) return false;
  const v = num(valorComDesconto);
  if (!(v > 0)) return false; // ainda não preenchido — não é violação
  return centavos(v) < centavos(minimo);
}

/** Valor do desconto contratual: valor cheio − valor contratado. */
export const valorDoDesconto = (valorNormal: unknown, percentual: unknown): number => {
  if (nenhumPreenchido(valorNormal, percentual)) return 0;
  return (num(valorNormal) * num(percentual)) / 100;
};

/** Abatimento por pagar até o vencimento = valor do desconto × pct. */
export const descontoAntecipado = (
  valorNormal: unknown,
  percentual: unknown,
  pctAntecipado: unknown = PCT_ANTECIPADO_PADRAO,
): string => {
  const d = valorDoDesconto(valorNormal, percentual);
  if (!(d > 0)) return "";
  const pct = num(pctAntecipado);
  if (!(pct > 0)) return "";
  return duasCasas((d * pct) / 100);
};

/**
 * Valor do boleto: o contratado **mais** o benefício de pontualidade que a
 * família perde se atrasar.
 *
 * Repare que é soma, não subtração. O valor contratado continua sendo o que
 * ela paga pagando em dia — esse número não se mexe.
 */
export const valorAposVencimento = (
  valorComDesconto: unknown,
  abatimento: unknown,
): string => {
  if (nenhumPreenchido(valorComDesconto)) return "";
  const v = num(valorComDesconto);
  if (!(v > 0)) return "";
  return duasCasas(v + num(abatimento));
};

/**
 * A primeira faixa do desconto: a parte que a família ganha de qualquer jeito,
 * pagando em dia ou não.
 *
 * É calculada **por diferença**, não como "85% do desconto". Assim a conta da
 * ficha sempre fecha no centavo: valor normal − condição exclusiva = boleto, e
 * boleto − benefício = o que ela paga. Se fossem dois arredondamentos
 * independentes, 85% e 15% poderiam somar um centavo a mais ou a menos que o
 * desconto total, e a família encontraria o erro antes de nós.
 */
export const descontoCondicaoExclusiva = (
  valorNormal: unknown,
  valorComDesconto: unknown,
  abatimento: unknown,
): string => {
  const boleto = num(valorAposVencimento(valorComDesconto, abatimento));
  if (!(boleto > 0)) return "";
  const vn = num(valorNormal);
  if (!(vn > 0)) return "";
  const faixa = vn - boleto;
  return faixa > 0 ? duasCasas(faixa) : "";
};

/**
 * Acréscimo do parcelamento do material, por faixa de parcelas.
 *
 * Até 3x a escola não cobra nada: antes o sistema aplicava 5% já na primeira
 * parcela, e quem pagava o material à vista levava acréscimo sem motivo.
 */
export const FAIXAS_MATERIAL: Array<[ate: number, taxa: number]> = [
  [3, 0],
  [6, 0.05],
  [12, 0.12],
];

/** Faixa que vale para um número de parcelas; acima da última, repete a maior. */
export const taxaMaterial = (parcelas: number): number =>
  FAIXAS_MATERIAL.find(([ate]) => parcelas <= ate)?.[1] ??
  FAIXAS_MATERIAL[FAIXAS_MATERIAL.length - 1][1];

/** "sem acréscimo" / "acréscimo de 5%" — o texto que a tela mostra. */
export const rotuloTaxaMaterial = (parcelas: number): string => {
  const t = taxaMaterial(parcelas);
  if (!(t > 0)) return "Sem acréscimo (até 3x)";
  const pct = Math.round(t * 100);
  const faixa = FAIXAS_MATERIAL.find(([, taxa]) => taxa === t);
  const anterior = FAIXAS_MATERIAL[FAIXAS_MATERIAL.findIndex(([, taxa]) => taxa === t) - 1];
  const de = anterior ? anterior[0] + 1 : 1;
  return `Acréscimo de ${pct}% (de ${de}x a ${faixa?.[0] ?? parcelas}x)`;
};

export const totalMaterial = (coc: unknown, bilingue: unknown): string =>
  nenhumPreenchido(coc, bilingue) ? "" : duasCasas(num(coc) + num(bilingue));

export const parcelaMaterial = (total: unknown, parcelas: unknown): string => {
  const t = num(total);
  const p = Math.trunc(num(parcelas));
  if (p <= 0 || t <= 0) return "";
  return duasCasas((t * (1 + taxaMaterial(p))) / p);
};

/** Investimento anual = (mensalidade com desconto × parcelas) + matrícula com desconto */
export const investimentoAnual = (
  mensalidadeComDesconto: unknown,
  parcelas: unknown,
  matriculaComDesconto: unknown,
): string =>
  nenhumPreenchido(mensalidadeComDesconto, parcelas, matriculaComDesconto)
    ? ""
    : duasCasas(num(mensalidadeComDesconto) * num(parcelas) + num(matriculaComDesconto));

/** Campos da proposta, todos em string — igual ao formulário do 4.x. */
export interface CamposProposta {
  valorNormal: string;
  percentualDesconto: string;
  valorComDesconto: string;
  valorMatricula: string;
  percentualDescontoMatricula: string;
  valorMatriculaComDesconto: string;
  qtdParcelas: string;
  valorAnual: string;

  valorNormalIntegral: string;
  percentualDescontoIntegral: string;
  valorComDescontoIntegral: string;
  valorMatriculaIntegral: string;
  percentualDescontoMatriculaIntegral: string;
  valorMatriculaComDescontoIntegral: string;
  qtdParcelasIntegral: string;
  valorAnualIntegral: string;

  materialCoc: string;
  materialBilingue: string;
  totalMaterial: string;
  qtdParcelasMaterial: string;
  valorParcelaMaterial: string;

  /**
   * Percentual do desconto antecipado usado nesta proposta. Fica gravado junto
   * com ela: se a escola mudar a política no ano que vem, a proposta impressa
   * hoje continua explicável.
   */
  percentualAntecipado: string;

  // derivados das duas faixas de desconto — só leitura na tela
  condicaoExclusiva: string;
  descontoAntecipado: string;
  valorAposVencimento: string;
  condicaoExclusivaMatricula: string;
  descontoAntecipadoMatricula: string;
  valorMatriculaAposVencimento: string;
  condicaoExclusivaIntegral: string;
  descontoAntecipadoIntegral: string;
  valorAposVencimentoIntegral: string;
  condicaoExclusivaMatriculaIntegral: string;
  descontoAntecipadoMatriculaIntegral: string;
  valorMatriculaAposVencimentoIntegral: string;
}

export const propostaVazia = (): CamposProposta => ({
  valorNormal: "", percentualDesconto: "", valorComDesconto: "",
  valorMatricula: "", percentualDescontoMatricula: "", valorMatriculaComDesconto: "",
  qtdParcelas: "", valorAnual: "",
  valorNormalIntegral: "", percentualDescontoIntegral: "", valorComDescontoIntegral: "",
  valorMatriculaIntegral: "", percentualDescontoMatriculaIntegral: "",
  valorMatriculaComDescontoIntegral: "", qtdParcelasIntegral: "", valorAnualIntegral: "",
  materialCoc: "", materialBilingue: "", totalMaterial: "",
  qtdParcelasMaterial: "", valorParcelaMaterial: "",
  percentualAntecipado: String(PCT_ANTECIPADO_PADRAO),
  condicaoExclusiva: "", descontoAntecipado: "", valorAposVencimento: "",
  condicaoExclusivaMatricula: "", descontoAntecipadoMatricula: "", valorMatriculaAposVencimento: "",
  condicaoExclusivaIntegral: "", descontoAntecipadoIntegral: "", valorAposVencimentoIntegral: "",
  condicaoExclusivaMatriculaIntegral: "", descontoAntecipadoMatriculaIntegral: "",
  valorMatriculaAposVencimentoIntegral: "",
});

/**
 * Recalcula todos os campos derivados a partir dos campos digitados.
 * Espelha exatamente a cascata do 4.x: desconto → matrícula → material → anual.
 */
export function recalcular(p: CamposProposta): CamposProposta {
  const n = { ...p };

  n.valorComDesconto = aplicarDesconto(n.valorNormal, n.percentualDesconto);
  n.valorComDescontoIntegral = aplicarDesconto(n.valorNormalIntegral, n.percentualDescontoIntegral);
  n.valorMatriculaComDesconto = aplicarDesconto(n.valorMatricula, n.percentualDescontoMatricula);
  n.valorMatriculaComDescontoIntegral = aplicarDesconto(
    n.valorMatriculaIntegral,
    n.percentualDescontoMatriculaIntegral,
  );

  // Desconto por pagar até o vencimento: incide sobre o valor do desconto.
  // Nenhum dos valores acima muda — o que se calcula aqui é quanto a família
  // perde se atrasar, e quanto o boleto passa a valer nesse caso.
  const pct = n.percentualAntecipado;
  n.descontoAntecipado = descontoAntecipado(n.valorNormal, n.percentualDesconto, pct);
  n.valorAposVencimento = valorAposVencimento(n.valorComDesconto, n.descontoAntecipado);
  n.condicaoExclusiva = descontoCondicaoExclusiva(n.valorNormal, n.valorComDesconto, n.descontoAntecipado);

  n.descontoAntecipadoMatricula = descontoAntecipado(n.valorMatricula, n.percentualDescontoMatricula, pct);
  n.valorMatriculaAposVencimento = valorAposVencimento(
    n.valorMatriculaComDesconto,
    n.descontoAntecipadoMatricula,
  );
  n.condicaoExclusivaMatricula = descontoCondicaoExclusiva(
    n.valorMatricula,
    n.valorMatriculaComDesconto,
    n.descontoAntecipadoMatricula,
  );

  n.descontoAntecipadoIntegral = descontoAntecipado(n.valorNormalIntegral, n.percentualDescontoIntegral, pct);
  n.valorAposVencimentoIntegral = valorAposVencimento(
    n.valorComDescontoIntegral,
    n.descontoAntecipadoIntegral,
  );
  n.condicaoExclusivaIntegral = descontoCondicaoExclusiva(
    n.valorNormalIntegral,
    n.valorComDescontoIntegral,
    n.descontoAntecipadoIntegral,
  );

  n.descontoAntecipadoMatriculaIntegral = descontoAntecipado(
    n.valorMatriculaIntegral,
    n.percentualDescontoMatriculaIntegral,
    pct,
  );
  n.valorMatriculaAposVencimentoIntegral = valorAposVencimento(
    n.valorMatriculaComDescontoIntegral,
    n.descontoAntecipadoMatriculaIntegral,
  );
  n.condicaoExclusivaMatriculaIntegral = descontoCondicaoExclusiva(
    n.valorMatriculaIntegral,
    n.valorMatriculaComDescontoIntegral,
    n.descontoAntecipadoMatriculaIntegral,
  );

  n.totalMaterial = totalMaterial(n.materialCoc, n.materialBilingue);
  n.valorParcelaMaterial = parcelaMaterial(n.totalMaterial, n.qtdParcelasMaterial);

  // o anual usa a mensalidade contratada, não a com pagamento em dia: é o
  // compromisso que a família assume, e não depende de nunca atrasar
  n.valorAnual = investimentoAnual(n.valorComDesconto, n.qtdParcelas, n.valorMatriculaComDesconto);
  n.valorAnualIntegral = investimentoAnual(
    n.valorComDescontoIntegral,
    n.qtdParcelasIntegral,
    n.valorMatriculaComDescontoIntegral,
  );

  return n;
}

const brl = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Texto do desconto na ficha impressa.
 *
 * Mudou junto com a regra: antes anunciava o desconto contratual inteiro como
 * se fosse o benefício de pagar em dia — o que assustava, porque era o mesmo
 * tamanho da punição por atrasar. Agora anuncia o abatimento de verdade.
 */
export function textoCondicaoExclusiva(
  percentual: unknown,
  valorNormal: unknown,
  valorComDesconto: unknown,
  pctAntecipado: unknown = PCT_ANTECIPADO_PADRAO,
): string {
  const p = num(percentual);
  if (!p) return "0%";
  const abatimento = num(descontoAntecipado(valorNormal, percentual, pctAntecipado));
  const boleto = num(valorAposVencimento(valorComDesconto, abatimento));
  if (!(abatimento > 0) || !(boleto > 0)) return `${p}%`;
  return `${p}% · boleto de ${brl(boleto)}, com desconto de ${brl(abatimento)} pagando até o vencimento`;
}
